import React, { useRef, useState, useEffect, useCallback } from 'react';
import { createPortal } from 'react-dom';
import { cn } from '../lib/utils';

/**
 * Instagram-style pinch-to-zoom image component.
 * - Only intercepts 2-finger gestures; single-finger scroll/swipe passes through
 * - When zooming, the image "breaks out" of its container via a portal overlay
 * - Dark backdrop dims the rest of the UI while zooming
 * - Snaps back smoothly on finger release
 * - Uses native non-passive touch listeners so preventDefault() works
 */
export default function PinchZoomImage({ src, alt, className, style, onClick, ...rest }) {
  const containerRef = useRef(null);
  const imgRef = useRef(null);
  const [transform, setTransform] = useState({ scale: 1, x: 0, y: 0 });
  const [isZooming, setIsZooming] = useState(false);
  const [isSnappingBack, setIsSnappingBack] = useState(false);
  const [imgRect, setImgRect] = useState(null);
  const [imgLoaded, setImgLoaded] = useState(false);
  const pinchRef = useRef(null);
  const wasPinchingRef = useRef(false);

  // Reset loaded state when src changes; check if already cached
  useEffect(() => {
    setImgLoaded(false);
    if (imgRef.current?.complete && imgRef.current?.naturalWidth > 0) {
      setImgLoaded(true);
    }
  }, [src]);

  const getDistance = (t1, t2) =>
    Math.hypot(t2.clientX - t1.clientX, t2.clientY - t1.clientY);

  const handleClick = useCallback((e) => {
    if (wasPinchingRef.current) {
      e.preventDefault();
      e.stopPropagation();
      wasPinchingRef.current = false;
      return;
    }
    if (onClick) onClick(e);
  }, [onClick]);

  useEffect(() => {
    const el = containerRef.current;
    if (!el) return;

    const onTouchStart = (e) => {
      if (e.touches.length === 2) {
        // Block browser zoom — set touch-action dynamically only during pinch
        el.style.touchAction = 'none';
        e.preventDefault();
        e.stopPropagation();

        // Capture the image's current position on screen for the portal overlay
        const img = imgRef.current;
        if (img) {
          const rect = img.getBoundingClientRect();
          const computed = window.getComputedStyle(img);
          setImgRect({
            top: rect.top,
            left: rect.left,
            width: rect.width,
            height: rect.height,
            objectPosition: computed.objectPosition || 'center center',
          });
        }

        const dist = getDistance(e.touches[0], e.touches[1]);
        const midX = (e.touches[0].clientX + e.touches[1].clientX) / 2;
        const midY = (e.touches[0].clientY + e.touches[1].clientY) / 2;

        pinchRef.current = {
          startDist: dist,
          startMidX: midX,
          startMidY: midY,
        };
        setIsZooming(true);
        setIsSnappingBack(false);
        wasPinchingRef.current = true;
      }
    };

    const onTouchMove = (e) => {
      if (e.touches.length === 2 && pinchRef.current) {
        e.preventDefault();
        e.stopPropagation();

        const dist = getDistance(e.touches[0], e.touches[1]);
        const midX = (e.touches[0].clientX + e.touches[1].clientX) / 2;
        const midY = (e.touches[0].clientY + e.touches[1].clientY) / 2;

        const scale = Math.min(4, Math.max(1, dist / pinchRef.current.startDist));
        const x = midX - pinchRef.current.startMidX;
        const y = midY - pinchRef.current.startMidY;

        setTransform({ scale, x, y });
      }
    };

    const onTouchEnd = (e) => {
      if (e.touches.length < 2 && pinchRef.current) {
        pinchRef.current = null;
        setIsZooming(false);
        // Start snap-back animation
        setIsSnappingBack(true);
        setTransform({ scale: 1, x: 0, y: 0 });
        // Restore normal touch behavior for single-finger scrolling/swiping
        el.style.touchAction = '';
        // Clean up after snap-back animation completes
        setTimeout(() => {
          setIsSnappingBack(false);
          setImgRect(null);
          wasPinchingRef.current = false;
        }, 300);
      }
    };

    // Non-passive so we can call preventDefault on 2-finger gestures
    el.addEventListener('touchstart', onTouchStart, { passive: false });
    el.addEventListener('touchmove', onTouchMove, { passive: false });
    el.addEventListener('touchend', onTouchEnd, { passive: true });
    el.addEventListener('touchcancel', onTouchEnd, { passive: true });

    return () => {
      el.removeEventListener('touchstart', onTouchStart);
      el.removeEventListener('touchmove', onTouchMove);
      el.removeEventListener('touchend', onTouchEnd);
      el.removeEventListener('touchcancel', onTouchEnd);
    };
  }, []);

  const showOverlay = isZooming || isSnappingBack;
  const backdropOpacity = isZooming ? Math.min(0.6, (transform.scale - 1) * 0.4) : 0;

  return (
    <>
      <div
        ref={containerRef}
        className={cn('pinch-zoom-container', className, !imgLoaded && 'bg-muted animate-pulse')}
        style={{ position: 'relative', overflow: 'hidden' }}
        onClick={handleClick}
      >
        <img
          ref={imgRef}
          src={src}
          alt={alt || ''}
          loading="lazy"
          onLoad={() => setImgLoaded(true)}
          className={cn(
            'transition-all duration-500 ease-out',
            imgLoaded ? 'blur-0 scale-100' : 'blur-sm scale-105',
            className
          )}
          style={{
            ...style,
            // Hide in-place image when portal overlay is active
            opacity: showOverlay ? 0 : imgLoaded ? 1 : 0,
          }}
          draggable={false}
          {...rest}
        />
      </div>

      {/* Portal overlay — renders at document root, escaping all overflow:hidden ancestors */}
      {showOverlay && imgRect && createPortal(
        <div
          className="pinch-zoom-overlay"
          style={{
            position: 'fixed',
            inset: 0,
            zIndex: 99999,
            pointerEvents: 'none',
          }}
        >
          {/* Dark backdrop */}
          <div
            style={{
              position: 'absolute',
              inset: 0,
              backgroundColor: 'black',
              opacity: backdropOpacity,
              transition: isZooming ? 'opacity 0.1s ease' : 'opacity 0.25s ease-out',
            }}
          />
          {/* Floating image positioned exactly where the original was */}
          <img
            src={src}
            alt={alt || ''}
            style={{
              position: 'absolute',
              top: imgRect.top,
              left: imgRect.left,
              width: imgRect.width,
              height: imgRect.height,
              objectFit: 'cover',
              objectPosition: imgRect.objectPosition,
              transform: `translate(${transform.x}px, ${transform.y}px) scale(${transform.scale})`,
              transition: isZooming ? 'none' : 'transform 0.25s ease-out',
              transformOrigin: 'center center',
              willChange: isZooming ? 'transform' : 'auto',
            }}
            draggable={false}
          />
        </div>,
        document.body
      )}
    </>
  );
}
